import { JMCLink } from "@components/JMCLink/JMCLink";
import { FullIMRComponent } from "@components/Layout/FullIMRComponent/FullIMRComponent";
import { iMRContext } from "@components/Layout/imrContext/imrContext";
const LoginToView = React.lazy(() => import("@components/LoginToView/LoginToView"));
const MissingData = React.lazy(() => import("@components/MissingData/MissingData"));
const SnackbarErrorMsg = React.lazy(() => import("@components/SnackbarErrorMsg/SnackbarErrorMsg"));
import { LogicSpacing } from "@components/Layout/LogicSpacing/LogicSpacing";
import { Spinner } from "@jmc/solid-design-system/src/components/atoms/Spinner/Spinner";
import { Typography } from "@jmc/solid-design-system/src/components/atoms/Typography/Typography";
import { Color } from "@jmc/solid-design-system/src/components/molecules/HeaderHero/HeaderHero";
import urlHelper from "@jmc/solid-design-system/src/utils/url-helper";
import useJnjBranding from "@jmc/utils/hooks/useJnjBranding";
import { default as stripSlash } from "@jmc/utils/utils/strip-slash";
import { ApplicationState } from "@redux/modules";
import { refetch as reloadAuthValid, ValidAuthResults } from "@redux/modules/authValid";
import { update } from "@redux/modules/bottomContent";
import { AuthProviders, CMSEventCenter, CMSMediaCenter, CMSNewsCenter, CMSUserProfile } from "@types";
import {
    CMSBlock,
    CMSEventCompanionProps,
    CMSMaterialProps,
    CMSProductGlossaryProduct,
    CMSRequestMaterials,
} from "@types";
import { isAuthorizedForAccessControl } from "@utils/authorizer";
import { lastSectionBackground } from "@utils/sectionColor";
import isEqual from "lodash/isEqual";
import React, { Suspense, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-use";
import regexifyString from "regexify-string";
import yn from "yn";

import { LOGIN_URL, REGISTER_URL } from "../../../plugins/JMCPageCreator/Constants";
import { load as loadProfile, refetch as reloadProfile } from "../../redux/modules/profile.ts";
import { AccessLevel } from "../../types/AccessLevel";
import { isValidUserProfile, UserProfile } from "../../utils/userProfiles";

interface PropTypes {
    children: Element | JSX.Element;
    gatedUrl?: string;
    publicContent?:
        | CMSBlock[]
        | CMSMediaCenter
        | CMSEventCenter
        | CMSNewsCenter
        | CMSProductGlossaryProduct
        | CMSRequestMaterials
        | CMSMaterialProps
        | CMSEventCompanionProps;
    content?: CMSBlock[];
    level?: AccessLevel;
    allowedUserProfiles?: CMSUserProfile[];
    accessOnly?: boolean;
    combinedMedical?: boolean;
    scientific?: boolean;
    eventNumber?: string;
    provider?: AuthProviders;
    paywall?: boolean;
    title?: string;
}

/**
 * Returns content as an object, for the freeFormPages of level 1
 *
 * @param content the public content to be wrapped
 */
export const wrapContentForFreeFormPage = (content: CMSBlock[]): { content: CMSBlock[] } => ({
    content,
});

interface NotAllowedMessageProps {
    commercial: boolean;
    title: string;
}

export const NotAllowedMessage = ({ commercial = false, title }: NotAllowedMessageProps): JSX.Element => {
    const { t } = useTranslation();

    const formatSnackbarText = (): (string | JSX.Element)[] => {
        const loginPlaceholder = "[Login]";
        const input = t("You are not eligible to see the content of this page.", {
            LOGIN: loginPlaceholder,
            REGISTER: "[Register]",
            ns: "common",
        });
        const decorateLink = (match: string): JSX.Element | string => {
            return (
                <JMCLink
                    commercial={commercial}
                    url={match === loginPlaceholder ? LOGIN_URL : REGISTER_URL}
                    name={match === loginPlaceholder ? "Login" : "Register"}
                    placement={title}
                >
                    <Typography color="inherit" variant="link">
                        {match === loginPlaceholder ? t("Login", { ns: "common" }) : t("Register", { ns: "common" })}
                    </Typography>
                </JMCLink>
            );
        };
        return regexifyString({
            pattern: /\[.*?\]/gim,
            decorator: decorateLink,
            input,
        });
    };

    return (
        <Suspense fallback={<div id={`suspense-div-not-allowed-message`} />}>
            <SnackbarErrorMsg textMsg={formatSnackbarText()} />
        </Suspense>
    );
};

const hasMarketingTokenInUrl = (): boolean => {
    if (typeof window === `undefined` || !window.location.search) return false;

    const qs = new URLSearchParams(window.location.search);

    return qs.has("token");
};

export const AccessContext = React.createContext<unknown | null>(null);

export const AccessControl = ({
    children,
    gatedUrl,
    level,
    publicContent,
    eventNumber,
    accessOnly,
    provider,
    allowedUserProfiles,
    combinedMedical,
    content,
    paywall,
    scientific,
    title,
}: PropTypes): JSX.Element => {
    const authValid = useSelector((state: ApplicationState) => state.authValid, isEqual);
    const profile = useSelector((state: ApplicationState) => state.profile, isEqual);
    const dispatch = useDispatch();
    const location = useLocation();
    const [gatedContent, setGatedContent] = useState(null);
    const [authorized, setAuthorized] = useState(undefined);
    const [dataNotShared, setDataNotShared] = useState(false);
    const [notAllowed, setNotAllowed] = useState(false);
    const [hasRefreshed, setHasRefreshed] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const { imr, marginXL } = useContext(iMRContext);
    const isTcpProvider = provider === AuthProviders.tcp;
    const { jnjFullBranded } = useJnjBranding();
    const [serviceLastSectionBg, setServiceLastSectionBg] = useState<Color | undefined>(undefined);

    const authorizing = (): void => {
        const authorizedResponse = isAuthorizedForAccessControl(
            authValid?.data as unknown as ValidAuthResults,
            level,
            gatedUrl,
            eventNumber,
            accessOnly,
            // TCP frontend supports more than one type of provider.
            isTcpProvider ? [provider, AuthProviders.pingTcp, AuthProviders.okta_tcp] : [provider],
        );
        setAuthorized(authorizedResponse);
    };

    const onCancelOrCloseLogin = (): void => {
        setNotAllowed(true);
    };

    useEffect(() => {
        //don't bother fetching profile and authdate if level 1
        if (level !== AccessLevel.level1) {
            //eslint warning suppressed to deal with uncaught in promise error in console

            dispatch(loadProfile());

            dispatch(reloadAuthValid());
        }

        if (imr?.code && jnjFullBranded) {
            dispatch(update({ hasVisibleIMR: true }));
        }
    }, []);

    // trying to refresh the token
    useEffect(() => {
        const authRefresh = (): void => {
            const origin = typeof window !== "undefined" ? process.env.GATSBY_API_ORIGIN_URL : "";
            if (typeof window !== "undefined") {
                setIsRefreshing(true);
                fetch(`${origin}/${process.env.GATSBY_API_VERSION}/auth/refresh`, {
                    method: "POST",
                    credentials: "include",
                    redirect: "manual",
                    headers: {
                        "X-ApiVersion": process.env.GATSBY_API_VERSION || "api",
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ redirectUrl: window.location.href }),
                })
                    .then((res) => {
                        if (res.status === 200) {
                            dispatch(reloadAuthValid());
                            // eslint-disable-next-line @typescript-eslint/no-unused-vars,@typescript-eslint/no-explicit-any
                            dispatch(reloadProfile()).catch((error: any) => {});
                        } else {
                            throw new Error(`${res.status}`);
                        }
                    })
                    .catch((error) => {
                        //happens if backend return 5xxx
                        setAuthorized(false);
                        // eslint-disable-next-line no-console
                        console.error(`auth refresh error: ${error}`);
                    })
                    .finally(() => {
                        setHasRefreshed(true);
                        setIsRefreshing(false);
                    });
            }
        };
        //if authvalid has error it will try to refresh the token, regardless of success or failure it will then see if you're authorized
        //if authorized has data instead it will check if the user has shared it's data if they logged in via doccheck
        if (authValid?.error && !authValid?.loading && level !== AccessLevel.level1 && !hasRefreshed && !isRefreshing) {
            authRefresh();
        }
    }, [authValid]);

    useEffect(() => {
        //must have some form of authdata to check that you're authorized and profile must not be loading (profile can be empty) to avoid race conditions in unit test
        if (!authValid?.loading && !profile?.loading && !isRefreshing) {
            authorizing();
            const blockedServices = ["my-jmc", "events", "learning-center", "transfer-of-value/access"];
            const url = accessOnly ? location?.pathname : gatedUrl;
            if (url) {
                const isBlockedService = blockedServices.some((service) => url.includes(service));
                const doccheckLogin = (authValid?.data as unknown as ValidAuthResults)?.results?.find(
                    (el) => el.authenticationType === "doccheck",
                );
                if (doccheckLogin && !doccheckLogin?.codsId && isBlockedService) {
                    setDataNotShared(true);
                }
            }
        } else if (authValid?.error && profile.error) {
            //if both contains error that mean the user is not logged in at all
            setAuthorized(false);
        }
    }, [authValid, profile]);

    useEffect(() => {
        let unmounted = false;
        if (authValid.loading || authValid.error || gatedContent) return;
        const getGatedUrl = (): string => {
            if (!gatedUrl) {
                return;
            }
            const { pathname } = urlHelper.parseUrl(gatedUrl) || {};
            const { origin } = location;
            return stripSlash.stripTrailing(origin) + pathname;
        };
        const finalgatedUrl = getGatedUrl();
        if (finalgatedUrl) {
            fetch(finalgatedUrl, {
                method: "GET",
                credentials: "include",
                headers: {
                    "X-ApiVersion": process.env.GATSBY_API_VERSION || "api",
                    "X-Paywall": paywall,
                },
            })
                .then((response) => {
                    if (response.ok) {
                        return response.json();
                    }
                })
                .then((data) => {
                    if (!unmounted) {
                        setGatedContent(data);
                    }
                })
                .catch((err) => {
                    throw new Error(`Gated content error: ${err}`);
                });
        }
        return () => {
            unmounted = true;
        };
    }, [gatedUrl, authValid]);

    // set the last section background to white if the page is a service page
    useEffect(() => {
        if (location.pathname.split("/").filter(Boolean).length <= 3 && location.pathname.includes("/services")) {
            setServiceLastSectionBg(Color.WHITE);
        }
    }, []);

    // returns either the page content or if there is none the public content
    if (yn(process.env.EVERYTHING_PUBLIC) || level === AccessLevel.level1) {
        if (jnjFullBranded) {
            const lastSectionBg = lastSectionBackground(content) ?? serviceLastSectionBg;
            dispatch(update({ lastSectionBackground: lastSectionBg }));
        }
        return (
            <AccessContext.Provider value={content ? wrapContentForFreeFormPage(content) : publicContent}>
                <>
                    {children}
                    {jnjFullBranded && <LogicSpacing />}
                    {imr?.code && <FullIMRComponent imr={imr} marginXL={marginXL} />}
                </>
            </AccessContext.Provider>
        );
    }

    // return a spinner while things load
    if (authValid.loading || profile.loading || isRefreshing || hasMarketingTokenInUrl() || authorized === undefined) {
        return (
            <>
                <div style={{ padding: "40vh" }}>
                    <Spinner size="xl" />
                </div>
                {imr?.code && <FullIMRComponent imr={imr} marginXL={marginXL} />}
            </>
        );
    }

    // render the not allowed snackbar when people refuse to login or when their current login is not good enough
    if (
        notAllowed ||
        (profile?.data &&
            level === AccessLevel.level3 &&
            !isValidUserProfile(profile?.data as unknown as UserProfile, allowedUserProfiles))
    ) {
        return paywall ? (
            <AccessContext.Provider value={"displayPaywallFragment"}>{children}</AccessContext.Provider>
        ) : (
            <>
                <NotAllowedMessage commercial={!combinedMedical} title={title} />
                {imr?.code && <FullIMRComponent imr={imr} marginXL={marginXL} />}
            </>
        );
    }

    if (authorized === false) {
        return (
            <>
                {paywall && (
                    <AccessContext.Provider value={"displayPaywallFragment"}>{children}</AccessContext.Provider>
                )}
                <Suspense fallback={<div id={`suspense-div-authorized-false`} />}>
                    <LoginToView
                        snackbar={onCancelOrCloseLogin}
                        anonymously={level === AccessLevel.level2}
                        medical={combinedMedical}
                        scientific={scientific}
                        provider={provider}
                    />
                </Suspense>
                {!paywall && imr?.code && <FullIMRComponent imr={imr} marginXL={marginXL} />}
            </>
        );
    }

    if (gatedContent || accessOnly) {
        if (dataNotShared) {
            return (
                <>
                    <Suspense fallback={<div id={`suspense-div-missing-data`} />}>
                        <MissingData />
                    </Suspense>
                    {imr?.code && <FullIMRComponent imr={imr} marginXL={marginXL} />}
                </>
            );
        }

        if (jnjFullBranded) {
            const lastSectionBg = lastSectionBackground(gatedContent?.content) ?? serviceLastSectionBg;
            dispatch(update({ lastSectionBackground: lastSectionBg }));
        }

        return (
            <AccessContext.Provider value={gatedContent}>
                {children}
                {jnjFullBranded && <LogicSpacing />}
                {imr?.code && <FullIMRComponent imr={imr} marginXL={marginXL} />}
            </AccessContext.Provider>
        );
    }

    return null;
};
